import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

function Step({ n, label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs flex items-center gap-1.5">
        <span className="inline-flex items-center justify-center h-4.5 w-4.5 min-w-[18px] rounded-full bg-slate-900 text-white text-[10px] font-bold">{n}</span>
        {label}
      </Label>
      {children}
    </div>
  );
}

export function GuidedProductDialog({ open, onOpenChange, onAdd }) {
  const [sel, setSel] = useState({ tprofile: "Off" });
  const [adding, setAdding] = useState(false);
  const { data: opts, isLoading, isError } = useQuery({
    queryKey: ["configurator-options"],
    queryFn: async () => (await api.get("/quotations/configurator-options")).data,
    enabled: open,
    staleTime: 30 * 60 * 1000,
  });

  const series = opts?.series || [];
  const s = series.find((x) => x.id === sel.series);
  const cats = s?.categories || [];
  const c = cats.find((x) => x.id === sel.category);
  const designs = c?.designs || [];
  const shades = (!designs.length && opts?.shades?.[c?.id]) || [];
  const d = designs.find((x) => x.code === sel.design);
  const sizes = s?.sizes || [];
  const thicknesses = s?.thicknesses || [];
  const embossForSize = sel.size ? opts?.emboss_by_size?.[sel.size] || null : null;
  const embossAvail = (d?.available_emboss?.length ? opts?.emboss || [] : [])
    .filter((e) => d.available_emboss.includes(e.id) && (!embossForSize || embossForSize.includes(e.id)));

  const set = (k, v) => {
    const reset = { series: ["category", "design", "shade", "size", "thickness", "emboss"], category: ["design", "shade", "emboss"], size: ["emboss"], design: ["emboss"] }[k] || [];
    setSel((p) => { const n = { ...p, [k]: v }; reset.forEach((r) => delete n[r]); return n; });
  };

  const canAdd = sel.series && sel.category && sel.size && sel.thickness &&
    (designs.length ? sel.design : shades.length ? sel.shade : true);

  const add = async () => {
    if (!canAdd) return;
    setAdding(true);
    const embossName = (opts?.emboss || []).find((e) => e.id === sel.emboss)?.name;
    const shadeName = shades.find((x) => x.id === sel.shade)?.name;
    const params = [
      { label: "Series", value: s.name },
      { label: "Category", value: c.name },
      ...(d ? [{ label: "Design", value: d.code + (d.color_name ? ` (${d.color_name})` : "") }] : []),
      ...(shadeName ? [{ label: "Shade", value: `${sel.shade} ${shadeName}`.trim() }] : []),
      { label: "Size", value: sel.size },
      { label: "Thickness", value: sel.thickness },
      ...(embossName ? [{ label: "Emboss", value: embossName }] : []),
      { label: "T-Profile", value: sel.tprofile },
    ];
    const name = "UniVicoustic - " + [s.name, c.name, d?.code, shadeName && `${shadeName} (${sel.shade})`, sel.size, sel.thickness, embossName]
      .filter(Boolean).join(" - ") + ` - T-Profile: ${sel.tprofile}`;
    let erp_product_code = "";
    if (d?.code) {
      try {
        const { data: hits } = await api.get(`/erp-items/search?q=${encodeURIComponent(d.code)}&limit=3`);
        if (hits?.length) erp_product_code = hits[0].item_code || "";
      } catch { /* no match */ }
    }
    let image = "", image_thumb = "";
    if (d?.thumb) {
      try {
        const { data: img } = await api.post("/quotations/configurator-pick", { image_url: d.thumb });
        image = img.storage_path || "";
        image_thumb = img.thumb_path || "";
      } catch { /* image optional */ }
    }
    onAdd({
      product_id: null, product_name: name, erp_product_code, unit: "Nos", qty: 1,
      list_price: 0, discount_pct: 0, final_price: 0,
      image, image_thumb, configurator_params: params,
    });
    setAdding(false);
    setSel({ tprofile: "Off" });
    onOpenChange(false);
    toast.success(erp_product_code ? `Product added — matched ERP code ${erp_product_code}` : "Product added — set the quantity and price");
  };

  const SelectBox = ({ k, items, placeholder, disabled, render, testid }) => (
    <Select value={sel[k] || ""} onValueChange={(v) => set(k, v)} disabled={disabled}>
      <SelectTrigger className="h-9" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-64">
        {items.map((it) => <SelectItem key={it.id ?? it} value={it.id ?? it}>{render ? render(it) : (it.name ?? it)}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setSel({ tprofile: "Off" }); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="guided-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Wand2 className="h-4 w-4 text-brand" /> Guided Product Picker</DialogTitle>
          <DialogDescription>Pick step by step — each choice filters the next. Options come live from the UniVicoustic configurator.</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : isError ? (
          <p className="text-sm text-red-500 py-6 text-center">Couldn't load the product catalogue — try again shortly.</p>
        ) : (
          <div className="space-y-3">
            <Step n={1} label="Series"><SelectBox k="series" items={series} placeholder="Pick a series…" testid="guided-series-select" /></Step>
            <Step n={2} label="Category"><SelectBox k="category" items={cats} placeholder={sel.series ? "Pick a category…" : "Pick a series first"} disabled={!sel.series} testid="guided-category-select" /></Step>
            {(!c || designs.length > 0) && (
              <Step n={3} label="Design / Print">
                <SelectBox k="design" items={designs.map((x) => ({ id: x.code, name: x.color_name ? `${x.code} — ${x.color_name}` : x.code }))}
                  placeholder={sel.category ? "Pick a design…" : "Pick a category first"} disabled={!designs.length} testid="guided-design-select" />
              </Step>
            )}
            {c && !designs.length && shades.length > 0 && (
              <Step n={3} label="Shade / Colour">
                <SelectBox k="shade" items={shades.map((x) => ({ id: x.id, name: x.id === x.name ? x.name : `${x.id} — ${x.name}` }))}
                  placeholder="Pick a shade…" testid="guided-shade-select" />
              </Step>
            )}
            {d?.thumb && (
              <div className="flex items-center gap-3 rounded-md border border-slate-200 p-2 bg-slate-50/60">
                <img src={d.thumb} alt={d.code} className="h-14 w-14 rounded object-cover border border-slate-200" data-testid="guided-design-preview" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                <p className="text-xs text-slate-500">{d.code}{d.color_name ? ` · ${d.color_name}` : ""} — this image is attached to the quote line.</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Step n={4} label="Size (mm)"><SelectBox k="size" items={sizes} placeholder="Size…" disabled={!sel.series} testid="guided-size-select" /></Step>
              <Step n={5} label="Thickness"><SelectBox k="thickness" items={thicknesses} placeholder="Thickness…" disabled={!sel.series} testid="guided-thickness-select" /></Step>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Step n={6} label="Emboss">
                {embossAvail.length ? (
                  <SelectBox k="emboss" items={embossAvail} placeholder="Optional…" testid="guided-emboss-select" />
                ) : (
                  <p className="h-9 flex items-center text-xs text-slate-400 border border-dashed border-slate-200 rounded-md px-3" data-testid="guided-emboss-na">Not applicable for this selection</p>
                )}
              </Step>
              <Step n={7} label="T-Profile"><SelectBox k="tprofile" items={["On", "Off"]} placeholder="T-Profile" testid="guided-tprofile-select" /></Step>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={add} disabled={!canAdd || adding} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="guided-add-btn">
            {adding && <Loader2 className="h-4 w-4 animate-spin" />}Add to Quote</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
